import React from 'react'
import { cn } from '@/lib/utils'
import { HelpTooltip } from '@/components/HelpTooltip'

/**
 * The one card shell every dashboard card renders through.
 *
 * WHY THIS EXISTS — the ragged-row bug. Each dashboard card used to be its own
 * `rounded-xl border … shadow-sm` div with no `h-full`, dropped into a grid cell
 * wrapped in `<div className="animate-slide-in">`. CSS Grid stretches the WRAPPER
 * to the row height; the card inside keeps its auto height, so a short card floats
 * with dead space under it while its neighbour fills the row. Fixing it card by
 * card guarantees the next card someone adds regresses it.
 *
 * The structural fix, applied once, here:
 *   • root  — `h-full flex flex-col`, so the card fills whatever box it is given
 *   • body  — `flex-1`, so it absorbs the slack instead of the card being short
 *   • footer— `mt-auto`, so a pinned action sits on the card's bottom edge
 *
 * `CardSlot` is the matching grid child: `animate-slide-in h-full` plus the
 * staggered delay. Use the pair together and rows are flush by construction.
 */

interface DashboardCardProps {
  /** Card title (`text-sm font-semibold`). Omit for a chrome-less shell. */
  title?: React.ReactNode
  /** Sub-line under the title (`text-xs text-muted-foreground`). */
  description?: React.ReactNode
  /** Leading glyph in the header, e.g. `<Activity className="h-4 w-4 …" />`. */
  icon?: React.ReactNode
  /** Renders a `HelpTooltip` beside the title. */
  help?: { content: string; learnMore?: string }
  /** Right-hand header slot — a `<Link>`, badge or small stat. */
  action?: React.ReactNode
  /** Pinned to the card's bottom edge (`mt-auto`), separated by a rule. */
  footer?: React.ReactNode
  /** Thin accent bar across the very top of the card, e.g. `bg-teal-500`. */
  accent?: string
  /** `false` drops the body padding — for flush lists, tables and dividers. */
  padded?: boolean
  className?: string
  bodyClassName?: string
  children?: React.ReactNode
}

export default function DashboardCard({
  title,
  description,
  icon,
  help,
  action,
  footer,
  accent,
  padded = true,
  className,
  bodyClassName,
  children,
}: DashboardCardProps) {
  const hasHeader = Boolean(title || description || action)

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm',
        'transition-shadow hover:shadow-md',
        className
      )}
    >
      {accent && <div className={cn('h-0.5 w-full shrink-0', accent)} />}

      {hasHeader && (
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/40 px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {icon}
              {title && <h3 className="text-sm font-semibold text-foreground">{title}</h3>}
              {help && <HelpTooltip content={help.content} learnMore={help.learnMore} />}
            </div>
            {description && (
              <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}

      <div className={cn('flex-1', padded && 'p-5', bodyClassName)}>{children}</div>

      {footer && (
        <div className="mt-auto shrink-0 border-t border-border/40 px-5 py-4">{footer}</div>
      )}
    </div>
  )
}

/**
 * Grid child for a dashboard card: the entrance animation plus the `h-full` that
 * lets `DashboardCard` reach the row height. Every card in a grid row goes in one.
 */
export function CardSlot({
  delay = 0,
  className,
  children,
}: {
  /** Stagger in ms — 0, 50, 100 … per the design system. */
  delay?: number
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('animate-slide-in h-full', className)} style={{ animationDelay: `${delay}ms` }}>
      {children}
    </div>
  )
}
